import { useLayoutEffect, useRef } from 'react';
import { AGENDA_COLUMNS, AGENDA_START_HOUR, AGENDA_TOTAL_MINUTES } from '../agendaConfig';
import type { useAgendaAppointmentMove } from '../useAgendaAppointmentMove';
import type { AgendaAppointmentInfoData } from './AgendaAppointmentInfo';
import type { AgendaMoveTarget } from '../agendaMove';
import type { AgendaMoveCheck } from '../useAgendaAppointmentMove';
import './AgendaAppointmentMove.css';

export type AgendaMoveController = ReturnType<typeof useAgendaAppointmentMove>;
// Shared presentation for moving a saved appointment and placing a series draft.
export type AgendaDragController = {
  moving: { info: AgendaAppointmentInfoData; practitionerId: string } | null;
  dragging: boolean;
  hoverTarget: AgendaMoveTarget | null;
  saving: boolean;
  notice: string;
  cancel: () => void;
  startDragging: () => void;
  setDragging: (value: boolean) => void;
  setHoverTarget: (target: AgendaMoveTarget | null) => void;
  check: (target: AgendaMoveTarget) => AgendaMoveCheck;
};
const time = (minute: number) => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
const date = (key: string) => key.split('-').reverse().join('.');
const provider = (id: string) => AGENDA_COLUMNS.find((column) => column.practitionerId === id)?.label || id;

export function AgendaMovePalette({ move, onDragMove, onPlace, placement = false }: {
  move: AgendaDragController;
  placement?: boolean;
  onDragMove: (x: number, y: number) => void;
  onPlace: (x: number, y: number) => void;
}) {
  const pointer = useRef<{ id: number; x: number; y: number; started: boolean } | null>(null);
  if (!move.moving) return null;
  const { info } = move.moving;
  return (
    <aside className="agenda-appointment-info agenda-move-palette" aria-label={placement ? 'Serientermin zum Platzieren' : 'Termin zum Verschieben'}
      style={info.color ? { backgroundColor: info.color.backgroundColor, borderColor: info.color.borderColor, color: info.color.textColor } : undefined}
      onContextMenu={(event) => { event.preventDefault(); event.stopPropagation(); }}>
      <button type="button" className="agenda-move-palette__cancel" aria-label={placement ? 'Platzierung abbrechen' : 'Verschiebung abbrechen'} onClick={move.cancel} disabled={move.saving}>
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="m6 6 8 8M14 6l-8 8" /></svg>
      </button>
      <div className="agenda-move-palette__drag" role="button" tabIndex={0} aria-label={`Termin ziehen: ${info.patientName}, ${info.title}, ${info.duration} Minuten`}
        onPointerDown={(event) => {
          if (move.saving || event.button !== 0) return;
          event.preventDefault();
          pointer.current = { id: event.pointerId, x: event.clientX, y: event.clientY, started: false };
          event.currentTarget.setPointerCapture?.(event.pointerId);
        }}
        onPointerMove={(event) => {
          const active = pointer.current;
          if (!active || active.id !== event.pointerId) return;
          if (!active.started && Math.hypot(event.clientX - active.x, event.clientY - active.y) < 5) return;
          if (!active.started) move.startDragging();
          active.started = true;
          onDragMove(event.clientX, event.clientY);
        }}
        onPointerUp={(event) => {
          const active = pointer.current;
          if (!active || active.id !== event.pointerId) return;
          pointer.current = null;
          if (active.started) onPlace(event.clientX, event.clientY);
          if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }}
        onPointerCancel={() => { pointer.current = null; move.setDragging(false); move.setHoverTarget(null); }}
        onLostPointerCapture={() => { pointer.current = null; move.setDragging(false); move.setHoverTarget(null); }}>
        <p className="agenda-move-palette__heading">{placement ? 'Serientermin platzieren' : 'Termin verschieben'}</p>
        <p className="agenda-move-palette__patient" title={info.patientName}>{info.patientName}</p>
        <p className="agenda-move-palette__treatment" title={info.title}>{info.title}</p>
        <p>{info.duration} Minuten · {provider(move.moving.practitionerId)}</p>
        <p className="agenda-move-palette__hint">In das Zeitraster ziehen</p>
      </div>
    </aside>
  );
}

export function AgendaMovePreview({ move }: { move: AgendaDragController }) {
  if (!move.moving || !move.dragging || !move.hoverTarget) return null;
  const target = move.hoverTarget, result = move.check(target), { info } = move.moving;
  return (
    <div className={`agenda-appointment-info agenda-move-preview agenda-move-preview--${result.status}`}
      data-move-minute={target.minute} data-move-date={target.date} data-move-provider={target.practitionerId} data-move-status={result.status}
      style={{ top: `${(target.minute - AGENDA_START_HOUR * 60) / AGENDA_TOTAL_MINUTES * 100}%`,
        height: `${info.duration / AGENDA_TOTAL_MINUTES * 100}%`, left: `${target.columnIndex / target.columnCount * 100}%`, width: `${100 / target.columnCount}%` }}>
      <span>{info.patientName} · {time(target.minute)}–{time(target.minute + info.duration)}</span>
    </div>
  );
}

export function AgendaMoveTargetStatus({ move }: { move: AgendaDragController }) {
  if (!move.moving) return null;
  if (!move.dragging || !move.hoverTarget) return move.notice ? <p className="agenda-appointment-info agenda-move-target-status" role="status">{move.notice}</p> : null;
  const target = move.hoverTarget, result = move.check(target);
  return (
    <div className={`agenda-appointment-info agenda-move-target-status agenda-move-target-status--${result.status}`} role="status">
      <strong>{target.practitionerId ? provider(target.practitionerId) : 'Behandler auswählen'} · {date(target.date)}</strong>
      <span>{time(target.minute)}–{time(target.minute + move.moving.info.duration)} Uhr · {move.moving.info.duration} Minuten</span>
      <span>{result.message}</span>
    </div>
  );
}

export function AgendaMoveConfirmation({ move }: { move: AgendaMoveController }) {
  const ref = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  useLayoutEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    backRef.current?.focus();
    return () => { if (previous?.isConnected) previous.focus(); };
  }, []);
  if (!move.moving || !move.pendingTarget) return null;
  const target = move.pendingTarget, { info } = move.moving;
  const originalStart = new Date(move.moving.original.when);
  const originalEnd = new Date(originalStart.getTime() + move.moving.original.duration * 60000);
  const originalTime = (value: Date) => value.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  const result = move.check(target);
  const back = () => { if (!move.saving) { move.setPendingTarget(null); move.setError(''); } };
  const choose = (practitionerId: string) => { move.setPendingTarget({ ...target, practitionerId }); move.setError(''); };
  return (
    <div className="agenda-move-confirmation-overlay" onClick={back}>
      <div ref={ref} className="agenda-appointment-info agenda-move-confirmation" role="dialog" aria-modal="true" aria-labelledby="agenda-move-confirmation-title"
        onClick={(event) => event.stopPropagation()} onKeyDown={(event) => {
          if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); back(); }
          if (event.key === 'Tab') {
            const controls = ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled), select:not(:disabled)');
            const first = controls?.[0], last = controls?.[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }
        }}>
        <h2 id="agenda-move-confirmation-title">Termin verschieben?</h2>
        <p>Möchten Sie diesen Termin auf das ausgewählte Datum und die neue Uhrzeit verschieben?</p>
        <dl>
          <dt>Patient</dt><dd>{info.patientName}</dd>
          <dt>Behandlung</dt><dd>{info.title}</dd>
          <dt><label htmlFor="agenda-move-practitioner">Behandler</label></dt>
          <dd>{target.candidates.length > 1 ? (
            <select id="agenda-move-practitioner" value={target.practitionerId} onChange={(event) => choose(event.target.value)} disabled={move.saving}>
              <option value="">Bitte auswählen</option>
              {target.candidates.map((id) => <option key={id} value={id}>{provider(id)}</option>)}
            </select>
          ) : provider(target.practitionerId)}</dd>
        </dl>
        <section className="agenda-move-confirmation__original" aria-label="Bisheriger Termin">
          <h3>Bisheriger Termin</h3>
          <p>{originalStart.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })} · {originalTime(originalStart)}–{originalTime(originalEnd)} Uhr · {provider(move.moving.practitionerId)}</p>
        </section>
        <section className="agenda-move-confirmation__new" aria-label="Neuer Termin">
          <h3>Neuer Termin</h3>
          <dl>
            <dt>Datum</dt><dd>{date(target.date)}</dd>
            <dt>Beginn</dt><dd>{time(target.minute)} Uhr</dd>
            <dt>Ende</dt><dd>{time(target.minute + info.duration)} Uhr</dd>
            <dt>Dauer</dt><dd>{info.duration} Minuten</dd>
          </dl>
        </section>
        <p className="agenda-move-confirmation__validation" role="status">{!target.practitionerId ? 'Bitte wählen Sie den Zielbehandler ausdrücklich aus.' : result.message}</p>
        {move.error ? <p className="agenda-move-confirmation__error" role="alert">{move.error}</p> : null}
        <div className="agenda-move-confirmation__actions">
          <button ref={backRef} type="button" onClick={back} disabled={move.saving}>Zurück</button>
          <button type="button" className="agenda-move-confirmation__submit" onClick={() => void move.confirm()}
            disabled={move.saving || !target.practitionerId || result.status !== 'valid'}>{move.saving ? 'Wird verschoben…' : 'Termin verschieben'}</button>
        </div>
      </div>
    </div>
  );
}

